import { z } from 'zod';
/**
 * Structured output contracts for the Reels pipeline (spec §12).
 * These mirror the purposes of the six supplied prompt templates:
 * title/concept, conversation + ten-second video JSON, caption/hashtags,
 * reference image, thumbnail, CapCut overlay.
 */
export const reelScenarioSchema = z.object({
    name: z.string().min(2).max(200),
    location: z.string().max(300).default(''),
    conflict: z.string().max(600).default(''),
    emotionalTone: z.string().max(200).default(''),
    coldOpen: z.string().max(600).default(''),
    finalHook: z.string().max(600).default(''),
    characterSet: z
        .array(z.object({ role: z.string().max(80), description: z.string().max(400) }))
        .default([]),
});
export const reelConceptSchema = z.object({
    title: z.string().min(3).max(300),
    titleTranslation: z.string().max(300).default(''),
    hookNumber: z.number().int().min(1).max(100).default(1),
    painTrigger: z.string().max(400).default(''),
    commentBaitAngle: z.string().max(400).default(''),
    castSuggestion: z.string().max(600).default(''),
    backgroundSet: z.string().max(600).default(''),
    respondentDetails: z.object({
        gender: z.string().max(40).default(''),
        ageRange: z.string().max(40).default(''),
        look: z.string().max(400).default(''),
        personality: z.string().max(400).default(''),
    }).default({}),
    scenarios: z.array(reelScenarioSchema).min(1).max(5),
});
export const dialogueLineSchema = z.object({
    speaker: z.string().max(80),
    text: z.string().max(500),
    startSec: z.number().min(0).max(120),
    endSec: z.number().min(0).max(120),
    gesture: z.string().max(300).default(''),
    camera: z.string().max(300).default(''),
});
export const segmentPlanSchema = z.object({
    partNumber: z.number().int().min(1).max(12),
    summary: z.string().max(600),
    dialogue: z.array(dialogueLineSchema).default([]),
    continuityNotes: z.string().max(800).default(''),
    /** Provider-agnostic ten-second video generation payload (JSON). */
    videoPrompt: z.record(z.unknown()),
});
export const conversationPlanSchema = z.object({
    conversationSummary: z.string().max(1500),
    microphoneNotes: z.string().max(400).default(''),
    cameraNotes: z.string().max(400).default(''),
    continuityRules: z.array(z.string().max(300)).default([]),
    segments: z.array(segmentPlanSchema).min(1).max(12),
    overlayPlan: z
        .array(z.object({
        text: z.string().max(120),
        startSec: z.number().min(0).max(120),
        endSec: z.number().min(0).max(120),
        position: z.string().max(60).default('center'),
        style: z.string().max(200).default(''),
        animation: z.string().max(120).default(''),
    }))
        .default([]),
});
export const referenceImageSpecSchema = z.object({
    imagePrompt: z.string().min(20).max(4000),
    negativePrompt: z.string().max(1000).default(''),
});
export const thumbnailSpecSchema = z.object({
    imagePrompt: z.string().min(20).max(4000),
    negativePrompt: z.string().max(1000).default(''),
    overlayText: z.string().max(200).default(''),
    overlayLayout: z.string().max(600).default(''),
});
export const reelCaptionSchema = z.object({
    caption: z.string().min(5).max(3000),
    hashtags: z.array(z.string().regex(/^#\S{1,60}$/)).min(3).max(12),
});
//# sourceMappingURL=reelPipeline.js.map